import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Send, HelpCircle, Bug, Lightbulb, CreditCard, MessageSquare, Clock, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "general", label: "General Help", icon: HelpCircle },
  { value: "bug", label: "Bug Report", icon: Bug },
  { value: "feature", label: "Feature Request", icon: Lightbulb },
  { value: "billing", label: "Billing / Subscription", icon: CreditCard },
  { value: "other", label: "Other", icon: MessageSquare },
];

const STATUS_CONFIG = {
  open: { label: "Open", color: "text-amber-400 bg-amber-400/10", icon: Clock },
  in_progress: { label: "In Progress", color: "text-blue-400 bg-blue-400/10", icon: Loader2 },
  resolved: { label: "Resolved", color: "text-green-400 bg-green-400/10", icon: CheckCircle2 },
};

const SupportPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", category: "general" });

  useEffect(() => { fetchTickets(); }, []);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`, { withCredentials: true });
      setTickets(res.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill in both the subject and message");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/ticket`, form, { withCredentials: true });
      toast.success("Ticket submitted! We'll get back to you soon.");
      setForm({ subject: "", message: "", category: "general" });
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit ticket");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border/50 bg-card/30">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-muted-foreground" data-testid="support-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
          </Button>
          <h1 className="text-xl font-serif font-bold">Support</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {/* Submit Ticket Form */}
        <Card className="bg-card border-border" data-testid="support-form-card">
          <CardContent className="p-6">
            <h2 className="text-lg font-serif mb-1">Need Help?</h2>
            <p className="text-sm text-muted-foreground mb-5">Submit a ticket and we'll get back to you as soon as possible.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Category</label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="support-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <span className="flex items-center gap-2"><c.icon className="w-3.5 h-3.5" /> {c.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Subject</label>
                <Input
                  value={form.subject}
                  onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                  placeholder="Brief description of your issue"
                  required
                  data-testid="support-subject"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Message</label>
                <Textarea
                  value={form.message}
                  onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  placeholder="Describe your issue in detail. Include any steps to reproduce if reporting a bug."
                  rows={5}
                  required
                  className="resize-none"
                  data-testid="support-message"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full sm:w-auto" data-testid="support-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Ticket
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Previous Tickets */}
        <div>
          <h2 className="text-lg font-serif mb-4">Your Tickets</h2>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No tickets yet. Submit one above if you need help!</p>
          ) : (
            <div className="space-y-3">
              {tickets.map(t => {
                const status = STATUS_CONFIG[t.status] || STATUS_CONFIG.open;
                const StatusIcon = status.icon;
                const catConfig = CATEGORIES.find(c => c.value === t.category);
                const CatIcon = catConfig?.icon || HelpCircle;
                return (
                  <Card key={t.id} className="bg-card border-border" data-testid={`ticket-${t.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <CatIcon className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
                          <div className="min-w-0">
                            <h3 className="font-medium text-sm truncate">{t.subject}</h3>
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{t.message}</p>
                            <p className="text-[10px] text-muted-foreground mt-1.5">
                              {new Date(t.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${status.color}`}>
                          <StatusIcon className="w-3 h-3" /> {status.label}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
